import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { getCurrentProject } from '@completion/selectors';
import { ReportService, ExcelService } from '@completion/services';
import { PunchReportRow } from '@completion/models';
import { weekNumber } from 'weeknumber';
import { LegendPosition } from '@swimlane/ngx-charts';
import { Table, TableFilterEvent } from 'primeng/table';

/** A single active column filter, shown as a removable chip above the grid (e.g. "Status: Open"). */
interface ActiveFilterChip {
  field: string;
  matchMode: string;
  label: string;
  valueText: string;
}

/**
 * A punch report row plus a precomputed `rowClasses` string (lifecycle tint + category accent).
 * Computed once per row when the data loads (see `withRowClasses`) rather than via a template
 * function call, so change detection doesn't have to re-run the classification logic on every
 * row on every check - it's just a plain string property binding.
 */
type PunchReportRowView = PunchReportRow & { rowClasses: string };

export type PunchReportGranularity = 'day' | 'week' | 'month';

interface PeriodBucket {
  key: string;
  label: string;
  start: Date;
  end: Date;
}

interface BurndownRightSeries {
  name: string;
  series: { name: string; value: number }[];
}

interface TaskOption {
  label: string;
  value: number | null;
}

@Component({
  selector: 'app-punch-report',
  templateUrl: './punch-report.component.html',
  styleUrls: ['./punch-report.component.scss'],
  standalone: false,
})
export class PunchReportComponent implements OnInit, OnDestroy {
  @ViewChild('dt') dt!: Table;

  private readonly destroy$ = new Subject<void>();
  private projectId: number | null = null;

  loading = false;
  error = false;

  allRows: PunchReportRowView[] = [];
  filteredRows: PunchReportRowView[] = [];

  /** Number of rows currently visible in the grid after global + column filters are applied. */
  visibleRowCount = 0;

  /** Column filters currently applied, shown as removable chips (e.g. "Status: Open"). */
  activeFilters: ActiveFilterChip[] = [];

  /** Human-readable labels for each filterable field, used both for chips and value formatting. */
  private readonly columnLabels: Record<string, string> = {
    taskNumber: 'Task',
    cpNumber: 'CP',
    mcpNumber: 'MCP',
    systemNumber: 'System',
    subSystemNumber: 'SubSystem',
    tagNumber: 'Tag',
    checkSheetName: 'CheckSheet',
    checkSheetItemName: 'Item',
    punchNumber: 'Punch #',
    category: 'Category',
    description: 'Description',
    raisedByCompany: 'RaisedBy',
    assignedUserName: 'AssignedTo',
    enteredByUserName: 'EnteredBy',
    clearedByUserName: 'ClearedBy',
    acceptedByUserName: 'AcceptedBy',
    open: 'Status',
  };

  taskOptions: TaskOption[] = [{ label: 'All tasks', value: null }];
  selectedTaskId: number | null = null;

  /** Excel-style checkbox filter options per column, built from the full (unfiltered) row set. */
  columnFilterOptions: Record<string, { label: string; value: string }[]> = {};
  statusFilterOptions: { label: string; value: boolean }[] = [
    { label: 'Open', value: true },
    { label: 'Closed', value: false },
  ];

  private readonly multiSelectFilterFields: (keyof PunchReportRow)[] = [
    'taskNumber',
    'cpNumber',
    'mcpNumber',
    'systemNumber',
    'subSystemNumber',
    'tagNumber',
    'checkSheetName',
    'checkSheetItemName',
    'category',
    'raisedByCompany',
    'assignedUserName',
    'enteredByUserName',
    'clearedByUserName',
    'acceptedByUserName',
  ];

  /**
   * User-adjustable column widths (px), set via PrimeNG's built-in column resize handles
   * (`pResizableColumn` / `[resizableColumns]`) and persisted to localStorage so they survive reloads.
   */
  columnWidths: Record<string, number> = {};
  private static readonly COLUMN_WIDTHS_STORAGE_KEY = 'punch-report-column-widths';

  /**
   * Whether the burndown charts are expanded. Defaults to collapsed (just the header bar
   * showing) to keep the punch grid above the fold; remembers the user's choice across visits.
   */
  burndownExpanded = false;
  private static readonly BURNDOWN_EXPANDED_STORAGE_KEY = 'punch-report-burndown-expanded';

  globalFilterValue = '';

  rows = 50;
  rowsPerPageOptions = [50, 100, 200, 500];

  granularity: PunchReportGranularity = 'week';
  granularityOptions: { label: string; value: PunchReportGranularity }[] = [
    { label: 'Day', value: 'day' },
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
  ];

  periodLabels: string[] = [];
  leftChartData: { name: string; series: { name: string; value: number }[] }[] = [];
  rightChartData: BurndownRightSeries[] = [];

  leftColorScheme: any = { domain: ['#003145'] };
  // Order matches the series pushed in computeBurndown(): A created, B created, A cleared,
  // B cleared, A accepted, B accepted. Created=red/orange, cleared=blue shades, accepted=green shades.
  rightColorScheme: any = { domain: ['#d72311', '#f57c00', '#1e88e5', '#64b5f6', '#2e7d32', '#66bb6a'] };
  legendPosition = LegendPosition.Below;

  // Right chart (Punch activity) sizing - widened/scrollable for the Day view, see computeBurndown().
  rightChartWidthPx: number | null = null;
  rightGroupPadding = 8;

  constructor(
    private readonly store: Store,
    private readonly reportService: ReportService,
    private readonly excelService: ExcelService,
  ) {}

  ngOnInit(): void {
    this.loadColumnWidths();
    this.loadBurndownExpandedState();
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        if (project?.id) {
          this.projectId = project.id;
          this.load();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Toggles the burndown section between its collapsed (header-only) and expanded state. */
  toggleBurndown(): void {
    this.burndownExpanded = !this.burndownExpanded;
    try {
      localStorage.setItem(PunchReportComponent.BURNDOWN_EXPANDED_STORAGE_KEY, String(this.burndownExpanded));
    } catch {
      // ignore storage errors (e.g. private browsing quota)
    }
  }

  private loadBurndownExpandedState(): void {
    try {
      this.burndownExpanded = localStorage.getItem(PunchReportComponent.BURNDOWN_EXPANDED_STORAGE_KEY) === 'true';
    } catch {
      this.burndownExpanded = false;
    }
  }

  /**
   * Handles PrimeNG's built-in column resize event (drag the column border to resize).
   * Persists the resulting width per column field to localStorage so it's remembered next visit.
   */
  onColumnResize(event: { element: HTMLElement }): void {
    const field = event.element?.getAttribute('data-field');
    if (!field) {
      return;
    }
    this.columnWidths = { ...this.columnWidths, [field]: event.element.offsetWidth };
    this.saveColumnWidths();
  }

  private loadColumnWidths(): void {
    try {
      const saved = localStorage.getItem(PunchReportComponent.COLUMN_WIDTHS_STORAGE_KEY);
      this.columnWidths = saved ? JSON.parse(saved) : {};
    } catch {
      this.columnWidths = {};
    }
  }

  private saveColumnWidths(): void {
    try {
      localStorage.setItem(PunchReportComponent.COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(this.columnWidths));
    } catch {
      // ignore storage errors (e.g. private browsing quota)
    }
  }

  load(): void {
    if (!this.projectId) {
      return;
    }
    this.loading = true;
    this.error = false;
    this.reportService
      .getPunchReport(this.projectId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.allRows = (rows || []).map((row) => this.withRowClasses(row));
          this.buildTaskOptions();
          this.buildColumnFilterOptions();
          this.applyTaskFilter();
          this.loading = false;
        },
        error: () => {
          this.allRows = [];
          this.filteredRows = [];
          this.loading = false;
          this.error = true;
        },
      });
  }

  refresh(): void {
    this.load();
  }

  private buildTaskOptions(): void {
    const seen = new Map<number, string>();
    this.allRows.forEach((row) => {
      if (row.taskId && !seen.has(row.taskId)) {
        seen.set(row.taskId, row.taskNumber ? `${row.taskNumber} - ${row.taskName || ''}`.trim() : `Task ${row.taskId}`);
      }
    });
    const options = Array.from(seen.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    this.taskOptions = [{ label: 'All tasks', value: null }, ...options];
  }

  onTaskFilterChange(): void {
    this.applyTaskFilter();
  }

  /**
   * Builds the Excel-style checkbox filter option lists (same UX as the "openPunches" table on the
   * dashboard: a searchable, multi-select checkbox dropdown per column). Options are derived from the
   * full, unfiltered row set so the list of choices doesn't shrink away as filters are applied.
   */
  private buildColumnFilterOptions(): void {
    const options: Record<string, { label: string; value: string }[]> = {};
    for (const field of this.multiSelectFilterFields) {
      const values = new Set<string>();
      this.allRows.forEach((row) => {
        const raw = row[field];
        if (raw !== null && raw !== undefined && raw !== '') {
          values.add(String(raw));
        }
      });
      options[field as string] = Array.from(values)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((v) => ({ label: v, value: v }));
    }
    this.columnFilterOptions = options;
  }

  private applyTaskFilter(): void {
    this.filteredRows = this.selectedTaskId ? this.allRows.filter((row) => row.taskId === this.selectedTaskId) : this.allRows;
    this.visibleRowCount = this.filteredRows.length;
    this.computeBurndown();
  }

  onGranularityChange(granularity: PunchReportGranularity): void {
    this.granularity = granularity;
    this.computeBurndown();
  }

  /**
   * Effective status for the Status column's status-circle: accepted punches show as "OK",
   * otherwise the punch's own category (PA/PB/PP/NA/OS) is shown.
   */
  getEffectiveStatus(row: PunchReportRow): string {
    return row.open ? row.category : 'OK';
  }

  /**
   * Precomputes the row's CSS classes once (at load time) instead of via a template function call:
   * a subtle background tint reflecting where the punch is in its lifecycle (open / cleared /
   * accepted), plus a solid left-edge accent in the punch's own category color (PA/PB/PP)
   * regardless of lifecycle state.
   */
  private withRowClasses(row: PunchReportRow): PunchReportRowView {
    const lifecycle = row.acceptedByTimestamp ? 'row-accepted' : row.clearedByTimestamp ? 'row-cleared' : 'row-open';
    const category =
      row.category === 'PA' ? 'category-pa' : row.category === 'PB' ? 'category-pb' : row.category === 'PP' ? 'category-pp' : '';
    return { ...row, rowClasses: category ? `${lifecycle} ${category}` : lifecycle };
  }

  /**
   * Fired whenever PrimeNG applies/changes a column filter (or the free-text quick-filter row).
   * Used to keep the "N / total" count and the active-filter chips above the grid in sync with
   * whatever is currently applied, without us having to duplicate PrimeNG's filtering logic.
   */
  onTableFilter(event: TableFilterEvent): void {
    this.visibleRowCount = Array.isArray(event.filteredValue) ? event.filteredValue.length : this.filteredRows.length;
    this.activeFilters = this.buildActiveFilters(event.filters);
  }

  private buildActiveFilters(filters: TableFilterEvent['filters']): ActiveFilterChip[] {
    const chips: ActiveFilterChip[] = [];
    if (!filters) {
      return chips;
    }
    for (const field of Object.keys(filters)) {
      const meta = filters[field];
      const value = meta?.value;
      const isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        continue;
      }
      chips.push({
        field,
        matchMode: meta?.matchMode || 'contains',
        label: this.columnLabels[field] || field,
        valueText: this.formatFilterValue(field, value),
      });
    }
    return chips;
  }

  private formatFilterValue(field: string, value: unknown): string {
    if (field === 'open' && Array.isArray(value)) {
      return value.map((v) => (v ? 'Open' : 'Closed')).join(', ');
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return String(value);
  }

  /** Removes a single active column filter (chip's [x]) without touching the others. */
  clearFilter(chip: ActiveFilterChip): void {
    this.dt.filter(null, chip.field, chip.matchMode);
  }

  /** Clears every column filter and the global search box in one go. */
  clearAllFilters(): void {
    this.globalFilterValue = '';
    this.dt.filters = {};
    this.dt._filter();
  }

  downloadExcel(): void {
    const excelData = this.filteredRows.map((row) => ({
      Task: row.taskNumber,
      CP: row.cpNumber,
      MCP: row.mcpNumber,
      System: row.systemNumber,
      SubSystem: row.subSystemNumber,
      Discipline: row.disciplineNumber,
      Area: row.areaName,
      Tag: row.tagNumber,
      CheckSheet: row.checkSheetNumber ? `${row.checkSheetNumber} ${row.checkSheetName || ''}`.trim() : row.checkSheetName,
      Item:
        row.checkSheetItemNumber != null ? `${row.checkSheetItemNumber} ${row.checkSheetItemName || ''}`.trim() : row.checkSheetItemName,
      PunchNo: row.punchNumber,
      Category: row.category,
      Description: row.description,
      Notes: row.notes,
      DueDate: row.due,
      RaisedBy: row.raisedByCompany,
      AssignedCompany: row.assignedCompany,
      AssignedTo: row.assignedUserName,
      EnteredBy: row.enteredByUserName,
      EnteredByDate: row.enteredByTimestamp,
      ClearedBy: row.clearedByUserName,
      ClearedByDate: row.clearedByTimestamp,
      AcceptedBy: row.acceptedByUserName,
      AcceptedByDate: row.acceptedByTimestamp,
      Open: row.open ? 'Open' : 'Closed',
    }));
    this.excelService.generateExcel(excelData, 'punch-report-' + Date.now().toString());
  }

  // ---- Burndown computation ----

  private buildPeriods(granularity: PunchReportGranularity, count: number): PeriodBucket[] {
    const periods: PeriodBucket[] = [];
    const now = new Date();

    if (granularity === 'day') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      for (let i = count - 1; i >= 0; i--) {
        const start = new Date(today);
        start.setDate(start.getDate() - i);
        const end = new Date(start);
        end.setDate(end.getDate() + 1);
        periods.push({
          key: start.toISOString().slice(0, 10),
          label: start.toLocaleDateString(undefined, { day: '2-digit', month: 'short' }),
          start,
          end,
        });
      }
    } else if (granularity === 'week') {
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const daysSinceMonday = (today.getDay() + 6) % 7;
      const mondayThisWeek = new Date(today);
      mondayThisWeek.setDate(mondayThisWeek.getDate() - daysSinceMonday);
      for (let i = count - 1; i >= 0; i--) {
        const start = new Date(mondayThisWeek);
        start.setDate(start.getDate() - i * 7);
        const end = new Date(start);
        end.setDate(end.getDate() + 7);
        const wn = weekNumber(start);
        periods.push({ key: `${start.getFullYear()}-W${wn}`, label: `Week ${wn}`, start, end });
      }
    } else {
      const firstOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      for (let i = count - 1; i >= 0; i--) {
        const start = new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth() - i, 1);
        const end = new Date(firstOfThisMonth.getFullYear(), firstOfThisMonth.getMonth() - i + 1, 1);
        periods.push({
          key: `${start.getFullYear()}-${start.getMonth() + 1}`,
          label: start.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
          start,
          end,
        });
      }
    }

    return periods;
  }

  private findBucketIndex(date: Date, periods: PeriodBucket[]): number {
    for (let i = 0; i < periods.length; i++) {
      if (date >= periods[i].start && date < periods[i].end) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Number of periods shown in the burndown for the current granularity:
   * 8 weeks, 8 months, or 4 weeks worth of days (28) for a more fine-grained daily view.
   */
  private getPeriodCount(granularity: PunchReportGranularity): number {
    switch (granularity) {
      case 'day':
        return 28;
      case 'month':
        return 8;
      case 'week':
      default:
        return 8;
    }
  }

  get burndownRangeLabel(): string {
    switch (this.granularity) {
      case 'day':
        return 'last 4 weeks';
      case 'month':
        return 'last 8 months';
      case 'week':
      default:
        return 'last 8 weeks';
    }
  }

  computeBurndown(): void {
    const periods = this.buildPeriods(this.granularity, this.getPeriodCount(this.granularity));
    this.periodLabels = periods.map((p) => p.label);

    const enteredTotal = new Array(periods.length).fill(0);
    const acceptedTotal = new Array(periods.length).fill(0);
    const enteredA = new Array(periods.length).fill(0);
    const enteredB = new Array(periods.length).fill(0);
    const clearedA = new Array(periods.length).fill(0);
    const clearedB = new Array(periods.length).fill(0);
    const acceptedA = new Array(periods.length).fill(0);
    const acceptedB = new Array(periods.length).fill(0);

    const windowStart = periods[0].start;
    let baseline = 0;

    for (const row of this.filteredRows) {
      const isA = row.category === 'PA';
      const isB = row.category === 'PB';
      const entered = row.enteredByTimestamp ? new Date(row.enteredByTimestamp) : null;
      const cleared = row.clearedByTimestamp ? new Date(row.clearedByTimestamp) : null;
      const accepted = row.acceptedByTimestamp ? new Date(row.acceptedByTimestamp) : null;

      if (entered) {
        if (entered < windowStart) {
          baseline++;
        } else {
          const idx = this.findBucketIndex(entered, periods);
          if (idx >= 0) {
            enteredTotal[idx]++;
            if (isA) enteredA[idx]++;
            else if (isB) enteredB[idx]++;
          }
        }
      }

      if (accepted) {
        if (accepted < windowStart) {
          baseline--;
        } else {
          const idx = this.findBucketIndex(accepted, periods);
          if (idx >= 0) {
            acceptedTotal[idx]++;
            if (isA) acceptedA[idx]++;
            else if (isB) acceptedB[idx]++;
          }
        }
      }

      if (cleared) {
        const idx = this.findBucketIndex(cleared, periods);
        if (idx >= 0) {
          if (isA) clearedA[idx]++;
          else if (isB) clearedB[idx]++;
        }
      }
    }

    let running = baseline;
    const openSeries: { name: string; value: number }[] = [];
    for (let i = 0; i < periods.length; i++) {
      running += enteredTotal[i] - acceptedTotal[i];
      openSeries.push({ name: periods[i].label, value: Math.max(running, 0) });
    }

    this.leftChartData = [{ name: 'Open punches', series: openSeries }];

    this.rightChartData = periods.map((p, i) => ({
      name: p.label,
      series: [
        { name: 'A created', value: enteredA[i] },
        { name: 'B created', value: enteredB[i] },
        { name: 'A cleared', value: clearedA[i] },
        { name: 'B cleared', value: clearedB[i] },
        { name: 'A accepted', value: acceptedA[i] },
        { name: 'B accepted', value: acceptedB[i] },
      ],
    }));

    // The Day view has up to 28 groups (vs. 8 for Week/Month), which squashes bars into slivers
    // if forced into the same container width. Give the chart more raw pixel width per group and
    // let the wrapper scroll horizontally instead, and tighten padding so bars stay nice and wide.
    if (this.granularity === 'day') {
      this.rightChartWidthPx = Math.max(420, periods.length * 34);
      this.rightGroupPadding = 4;
    } else {
      this.rightChartWidthPx = null;
      this.rightGroupPadding = 8;
    }
  }
}
